'use client';

import { useRef, useState, useCallback, useEffect, type RefObject } from 'react';

export interface ItemRect {
  top: number;
  height: number;
  left: number;
  width: number;
}

interface InternalItemRect extends ItemRect {
  disabled: boolean;
}

interface UseProximityHoverOptions {
  axis?: 'x' | 'y';
}

interface UseProximityHoverReturn {
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
  itemRects: ItemRect[];
  sessionRef: RefObject<number>;
  handlers: {
    onMouseMove: (e: React.MouseEvent) => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  registerItem: (index: number, element: HTMLElement | null, disabled?: boolean) => void;
  measureItems: () => void;
}

export function useProximityHover<T extends HTMLElement>(
  containerRef: RefObject<T | null>,
  options: UseProximityHoverOptions = {},
): UseProximityHoverReturn {
  const { axis = 'y' } = options;
  // Track per-item state alongside the element so we can suppress hover for
  // disabled items: mouse-over-them sets activeIndex to null instead of
  // falling back to the closest enabled neighbor.
  const itemsRef = useRef(new Map<number, { element: HTMLElement; disabled: boolean }>());
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [itemRects, setItemRects] = useState<ItemRect[]>([]);
  const itemRectsRef = useRef<(InternalItemRect | undefined)[]>([]);
  const sessionRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  const registerItem = useCallback(
    (index: number, element: HTMLElement | null, disabled: boolean = false) => {
      if (element) {
        itemsRef.current.set(index, { element, disabled });
      } else {
        itemsRef.current.delete(index);
      }
    },
    [],
  );

  const measureItems = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rects: (InternalItemRect | undefined)[] = [];
    itemsRef.current.forEach(({ element, disabled }, index) => {
      // Use offset* instead of getBoundingClientRect so measurements are
      // unaffected by CSS transforms (e.g. scaleY animation on the parent
      // motion.div). offsetTop/offsetLeft are layout values relative to the
      // offsetParent (the scroll container), matching the coordinate space
      // used by `position: absolute` children.
      rects[index] = {
        top: element.offsetTop,
        height: element.offsetHeight,
        left: element.offsetLeft,
        width: element.offsetWidth,
        disabled,
      };
    });
    itemRectsRef.current = rects;
    setItemRects(
      rects.map(
        (r) => (r ? { top: r.top, height: r.height, left: r.left, width: r.width } : r) as ItemRect,
      ),
    );
  }, [containerRef]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const container = containerRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const mousePos = axis === 'x' ? mouseX : mouseY;

        let closestIndex: number | null = null;
        let closestDistance = Infinity;
        let containingIndex: number | null = null;
        let containingDisabled = false;

        const rects = itemRectsRef.current;
        // Convert content-relative rects to viewport coords using live scroll
        const scrollOffset = axis === 'x' ? container.scrollLeft : container.scrollTop;
        const borderOffset = axis === 'x' ? container.clientLeft : container.clientTop;
        const containerEdge = axis === 'x' ? containerRect.left : containerRect.top;

        for (let index = 0; index < rects.length; index++) {
          const r = rects[index];
          if (!r) continue;

          const contentPos = axis === 'x' ? r.left : r.top;
          const itemStart = containerEdge + borderOffset + contentPos - scrollOffset;
          const itemSize = axis === 'x' ? r.width : r.height;
          const itemEnd = itemStart + itemSize;

          if (mousePos >= itemStart && mousePos <= itemEnd) {
            containingIndex = index;
            containingDisabled = r.disabled;
          }

          // Skip disabled items in the closest fallback so a disabled tab in
          // the middle of the strip can't steal hover for an enabled
          // neighbor when the cursor sits inside the disabled tab.
          if (r.disabled) continue;

          const itemCenter = itemStart + itemSize / 2;
          const distance = Math.abs(mousePos - itemCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        }

        // Mouse is inside a disabled item: do NOT report a neighbor as the
        // hovered index. Disabled items shouldn't react and shouldn't
        // bleed hover into siblings either.
        if (containingDisabled) {
          setActiveIndex(null);
          return;
        }

        setActiveIndex(containingIndex ?? closestIndex);
      });
    },
    [axis, containerRef],
  );

  const handleMouseEnter = useCallback(() => {
    sessionRef.current += 1;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setActiveIndex(null);
  }, []);

  // Clean up rAF on unmount
  useEffect(
    () => () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    },
    [],
  );

  return {
    activeIndex,
    setActiveIndex,
    itemRects,
    sessionRef,
    handlers: {
      onMouseMove: handleMouseMove,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
    registerItem,
    measureItems,
  };
}

/**
 * Hook for child items to register themselves with the proximity hover system.
 * Call in useEffect with the item's ref and index.
 */
export function useRegisterProximityItem(
  registerItem: (index: number, element: HTMLElement | null, disabled?: boolean) => void,
  index: number,
  ref: RefObject<HTMLElement | null>,
  disabled?: boolean,
) {
  useEffect(() => {
    registerItem(index, ref.current, disabled);
    return () => registerItem(index, null);
  }, [index, registerItem, ref, disabled]);
}
